/**
 * The canonical record's measured context (#2267 port 6).
 *
 * Every figure here is computed by `core.dashboard_measures` under the caller's
 * own RLS, through the shared `MeasureStrip`. Nothing is reduced in the browser,
 * which is the whole point: the legacy CRM header pulled a 500-row page of
 * three tables on every party open and aggregated them locally, so past 500
 * rows every tile was wrong — "plausible, precise, and silent", as
 * `MeasureStrip`'s own header puts it.
 *
 * `recordMeasurePlan` decides WHICH registered measures may be asked for, and
 * its header carries the measurement behind that decision — the short version
 * is that `core.compute_measure` takes a key and no record, so a `$record`-bound
 * measure has no server-side source and is withheld rather than answered with a
 * deployment-wide number wearing this record's label.
 *
 * ── one state, two places (#2406) ──────────────────────────────────────────
 * The owner ruled the cap too tall and the collapse control too loud. Both had
 * the same cause: the toggle was a bordered, filled button on a row of its own
 * INSIDE the insight slot, so collapsing the figures left a band holding only
 * the button. The leading design (#2406, temp-design Organization Detail)
 * answers both: the control is a thin engraved row — "⌄ METRICS … Click to
 * collapse" — that `RecordShell` draws from its `insightDisclosure` descriptor,
 * and the strip stays in `insight`. Two places, one state: `useRecordMeasures`
 * is that state, called once by the record body and handed to both, so they
 * cannot disagree about whether there is anything to show or whether it is
 * collapsed.
 *
 * ── the collapse preference survives, the client-side maths does not ────────
 * The legacy header remembered whether you had collapsed it. That is a genuine
 * preference about a person's own screen and it is kept, in the same
 * `localStorage` key, so nobody's header silently re-expands on the cutover.
 * Everything else about the legacy component is gone.
 */
import { useId, useState } from 'react'
import type { Row } from '@solum/sdk'
import { MeasureStrip } from '../data/MeasureStrip'
import { useRelated } from '../data/queries'
import { recordMeasurePlan, type RecordMeasureTile } from './recordMeasureTiles'
import s from './RecordMeasures.module.css'

/** The legacy key, deliberately. A person who collapsed this header yesterday
 *  has it collapsed today; a new key would silently reset everybody. */
const COLLAPSED_KEY = 'solum.hdr.metrics'

const readCollapsed = (): boolean => {
  try { return localStorage.getItem(COLLAPSED_KEY) === '1' } catch { return false }
}

export interface RecordMeasuresModel {
  /**
   * `none` — nothing computable, or not asked yet: no toggle and no strip.
   * `unavailable` — the registry read was refused: one bounded sentence, no toggle.
   * `ready` — at least one measure the server can compute.
   */
  status: 'none' | 'unavailable' | 'ready'
  tiles: RecordMeasureTile[]
  collapsed: boolean
  toggle: () => void
  /** The strip's element id — what the toggle names in `aria-controls`. */
  stripId: string
}

/**
 * The header's measures, for one entity: what to show, and whether it is shown.
 *
 * Called ABOVE the record body's early returns — it is a hook, and a hook
 * below a conditional return is the blank page `RecordBody` already records
 * once. `enabled` is false for a record that does not exist yet.
 */
export function useRecordMeasures(entity: string, enabled: boolean): RecordMeasuresModel {
  const measures = useRelated('core', 'measure', { entity_type: entity }, enabled)
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const stripId = useId()

  const toggle = () => {
    const next = !collapsed
    try { localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0') } catch { /* private mode */ }
    setCollapsed(next)
  }
  const model = (status: RecordMeasuresModel['status'], tiles: RecordMeasureTile[] = []) =>
    ({ status, tiles, collapsed, toggle, stripId })

  /* BOUNDED (the legacy component got this right and it is kept): metrics are
     an optional ornament, so a refused registry read must not blank the record
     underneath it, and must not be an uncaught error either — that is how a
     column rename went unnoticed on every record page at once. */
  if (!enabled) return model('none')
  if (measures.isError) return model('unavailable')
  if (measures.isLoading) return model('none')

  /* Nothing computable means nothing drawn — including the case where this
     entity's only registered measures are record-bound. An explanation of a
     platform gap is not a thing to put on a person's record; the gap belongs in
     the ledger and on the issue, and it is recorded in both. */
  const plan = recordMeasurePlan(Array.isArray(measures.data) ? (measures.data as Row[]) : [])
  return plan.computable.length === 0 ? model('none') : model('ready', plan.computable)
}


/**
 * The figures, for the insight slot.
 *
 * Collapsed, the strip's element STAYS and is `hidden`: the toggle names it in
 * `aria-controls`, and a reference to an element that is not there is a
 * disclosure pointing at nothing. `RecordShell` gives the slot no room while
 * its only child is hidden, so the cap is the identity row alone. The
 * `MeasureStrip` inside is not mounted, so a collapsed header asks the server
 * for nothing.
 */
export function RecordMeasures({ model }: { model: RecordMeasuresModel }) {
  if (model.status === 'unavailable') {
    return (
      <p className={s.unavailable} data-testid="record-measures-error">
        Metrics are unavailable for this record type.
      </p>
    )
  }
  if (model.status !== 'ready') return null
  return (
    <section id={model.stripId} data-testid="record-measures"
      data-collapsed={model.collapsed || undefined} hidden={model.collapsed}>
      {!model.collapsed && <MeasureStrip tiles={model.tiles} />}
    </section>
  )
}
